import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Propose a points adjustment — the maker half of a pair.
 *
 * ── THIS DIALOG MOVES NOTHING, AND SAYS SO ────────────────────────────────
 *
 * The button used to be disabled because the gate it promised could not exist:
 * an adjustment landed in `points_ledger`, which has no status or approver
 * columns. The fix was not to add them to a ledger — a ledger row is a movement
 * that HAPPENED — but to give the proposal its own table, which is what the
 * money side has always done.
 *
 * So pressing Raise writes a REQUEST. A second admin holding `points.verdict`
 * has to sign it before a single point moves, and the confirmation says that
 * rather than letting somebody walk away believing the player has been paid.
 */
const REASONS = [
  { code: 'SERVICE_RECOVERY', label: 'Service recovery' },
  { code: 'PROMOTION_CORRECTION', label: 'Promotion correction' },
  { code: 'SYSTEM_ERROR', label: 'Our error' },
  { code: 'MANUAL_AWARD', label: 'Manual award' },
  { code: 'CLAWBACK_ABUSE', label: 'Clawback — abuse' },
  { code: 'BALANCE_CORRECTION', label: 'Balance correction' },
] as const;

/** One row the roster gave back for what the operator typed. */
interface PlayerMatch {
  id: string;
  ref: string;
  name: string;
  /* The roster calls it brandName. Named the same here so the two cannot
     quietly disagree — an undefined `brand` would have rendered nothing at all
     and looked like a player with no brand. */
  brandName: string;
}

export function PointsAdjustmentDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  /**
   * WHO THIS IS FOR — SEARCHED, NOT TYPED FROM MEMORY.
   *
   * This field asked for the internal id: thirty-six characters of UUID that
   * appear on no ticket, no email and no conversation with a player. An
   * operator had to open the roster, find the person, open their profile, copy
   * a string nobody can check by eye, and come back — for what is otherwise a
   * thirty-second job. Reported in QA as "it's hard for the user to find player
   * id, there must be a search to find the player", which is the whole of it.
   *
   * The roster's own endpoint does the looking, so brand scope applies without
   * this screen knowing about it: a player outside the operator's brands simply
   * does not come back. `chosen` holds the resolved row — the id still goes to
   * the API, because a reference is a display code and the request has to name
   * the row.
   */
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<PlayerMatch | null>(null);
  const [matches, setMatches] = useState<PlayerMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [direction, setDirection] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [points, setPoints] = useState('');
  const [reasonCode, setReasonCode] = useState<string>(REASONS[0].code);
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setChosen(null);
    setMatches(null);
    setDirection('CREDIT');
    setPoints('');
    setReasonCode(REASONS[0].code);
    setNote('');
    setFields({});
  }, [open]);

  /**
   * WHAT THE ROSTER SAYS, for whatever was typed.
   *
   * Run on demand rather than on every keystroke: this is a dialog somebody
   * opens to do one thing, and a request per character is a request per
   * character. `perPage` is small because a list nobody can scan is the same
   * problem the id field had.
   *
   * An empty result is REPORTED AS ITSELF — "nothing is filed under that" — and
   * not as an error, because the usual cause is a reference from another brand,
   * which the operator can do nothing about and should not be told is a typo.
   */
  const lookUp = async () => {
    const term = search.trim();
    if (term === '') {
      setFields({ playerId: 'Type a name or a reference like PL-10241 first.' });
      return;
    }
    setFields({});
    setSearching(true);
    try {
      const found = await api.get<{ items: PlayerMatch[] }>(
        `/api/players?q=${encodeURIComponent(term)}&perPage=8`,
      );
      setMatches(found.items);
      /* ONE EXACT REFERENCE PICKS ITSELF. Typing PL-10241 and then having to
         click the only row it returned is a step that exists for the screen's
         benefit rather than the operator's. A name that happens to match one
         person does NOT auto-pick: names repeat, and picking the wrong player
         is the mistake this whole dialog is arranged to prevent. */
      const exact = found.items.filter((m) => m.ref.toLowerCase() === term.toLowerCase());
      if (exact.length === 1) setChosen(exact[0]!);
    } catch (error: unknown) {
      showError({
        title: 'The roster could not be searched',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing has been changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    const problems: Record<string, string> = {};
    if (chosen === null) problems['playerId'] = 'Find the player this is for, and pick them.';
    if (!/^[1-9][0-9]*$/.test(points.trim())) {
      problems['points'] = 'Enter the number of points as a whole number above zero.';
    }
    if (note.trim().length < 10) problems['note'] = 'Say what happened — at least 10 characters.';
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }
    setFields({});

    setBusy(true);
    try {
      const result = await api.post<{
        ref: string;
        player: string;
        points: string;
        direction: string;
        balanceWhenRaised: string;
      }>('/api/points/adjustments', {
        playerId: chosen!.id,
        direction,
        points: points.trim(),
        reasonCode,
        note: note.trim(),
      });
      toast(
        `${result.ref} raised — ${result.direction === 'CREDIT' ? 'give' : 'take'} ${formatCount(
          BigInt(result.points),
        )} points ${result.direction === 'CREDIT' ? 'to' : 'from'} ${result.player}. ` +
          'NOTHING HAS MOVED: a second admin has to sign it.',
      );
      onDone();
      onClose();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'The adjustment was not raised',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title="Propose a points adjustment"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={busy} onClick={() => void submit()}>
            Raise it for signing
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice">
          <Icon name="user-check" />
          <div>
            <strong>This moves no points.</strong> It raises a request that a second admin holding{' '}
            <span className="signin-perm">points.verdict</span> has to sign — and it cannot be you,
            whichever keys you hold. Until somebody signs it the player&rsquo;s balance is exactly
            what it was.
          </div>
        </div>

        {chosen ? (
          /* PICKED. The name is shown back, because "PL-10241" read off a
             ticket and the person it belongs to are two different things to be
             sure of, and the operator is about to move somebody's points. */
          <Field label="Player" error={fields['playerId']}>
            {() => (
              <div className="row">
                <strong>{chosen.name}</strong>
                <span className="text-subtle">
                  {chosen.ref}
                  {chosen.brandName ? ` · ${chosen.brandName}` : ''}
                </span>
                <Button
                  className="header-spacer"
                  onClick={() => {
                    setChosen(null);
                    setMatches(null);
                    setSearch('');
                  }}
                >
                  Change
                </Button>
              </div>
            )}
          </Field>
        ) : (
          <Field
            label="Player"
            error={fields['playerId']}
            hint="A name or a reference like PL-10241. The balance is shown back to you when the request is raised."
          >
            {({ id, describedBy, invalid }) => (
              <>
                <div className="row">
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    value={search}
                    placeholder="PL-10241, or a name"
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      /* Enter searches rather than submitting the dialog. A
                         half-filled form sent by the key that normally means
                         "next field" is how somebody raises the wrong thing. */
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void lookUp();
                      }
                    }}
                  />
                  <Button busy={searching} disabled={searching} onClick={() => void lookUp()}>
                    <Icon name="magnifying-glass" />
                    Find
                  </Button>
                </div>

                {matches !== null && matches.length === 0 && (
                  <p className="text-subtle" style={{ fontSize: '11.5px' }}>
                    Nothing is filed under “{search.trim()}” in your brand scope. A player on a
                    brand you do not hold will not appear here.
                  </p>
                )}

                {matches !== null && matches.length > 0 && (
                  <ul className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {matches.map((m) => (
                      <li key={m.id}>
                        <button
                          type="button"
                          className="queue-pill"
                          style={{ width: '100%', textAlign: 'left' }}
                          onClick={() => setChosen(m)}
                        >
                          <span className="queue-pill-value" style={{ fontSize: '14px' }}>
                            {m.name}
                          </span>
                          <span className="queue-pill-meta">
                            {m.ref}
                            {m.brandName ? ` · ${m.brandName}` : ''}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </Field>
        )}

        <div className="halves">
          <Field label="Direction" hint="Credit gives points. Debit takes them.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={direction}
                onChange={(e) => setDirection(e.target.value === 'DEBIT' ? 'DEBIT' : 'CREDIT')}
              >
                <option value="CREDIT">Credit — give points</option>
                <option value="DEBIT">Debit — take points back</option>
              </Select>
            )}
          </Field>
          <Field
            label="Points"
            error={fields['points']}
            hint="A whole number, always positive — the direction above decides the sign."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="500"
              />
            )}
          </Field>
        </div>

        {direction === 'DEBIT' ? (
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              A debit is checked against the balance <strong>when it is signed</strong>, not now.
              Nothing is held back in the meantime, so a player who spends their points while this
              waits will have spent them and the request will be refused — which is the honest
              outcome rather than a reservation nobody told them about.
            </div>
          </div>
        ) : null}

        <Field
          label="Reason"
          error={fields['reasonCode']}
          hint="A closed set, so a report can count adjustments by cause."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="What happened"
          error={fields['note']}
          hint="At least 10 characters, and the thing the admin signing this will actually read before deciding."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ticket TK-00412 — points not awarded on a settled bet."
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
